/**
 * useSEO Hook for PinDay
 * React 19 compatible hook for managing SEO meta tags
 */

import { useEffect } from 'react'
import type { SupportedLanguage, FAQItem, BreadcrumbItem } from './types'
import { 
  SITE_URL, 
  DEFAULT_OG_IMAGE, 
  LANGUAGE_LOCALES, 
  SCHEMA_ORG_CONFIG,
  getHreflangLinks,
  getCanonicalUrl,
} from './config'

interface UseSEOOptions {
  title: string
  description: string
  keywords?: string[]
  language: SupportedLanguage
  path: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'product'
  noindex?: boolean
  breadcrumbs?: BreadcrumbItem[]
  faq?: FAQItem[]
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
  }
}

/**
 * Helper to create or update a meta tag
 */
function setMetaTag(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name'
  let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement
  
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, name)
    document.head.appendChild(tag)
  }
  
  tag.content = content
}

/**
 * Helper to create or update a link tag
 */
function setLinkTag(rel: string, href: string, extra?: Record<string, string>) {
  const selector = extra?.hreflang 
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`
  
  let tag = document.querySelector(selector) as HTMLLinkElement
  
  if (!tag) {
    tag = document.createElement('link')
    tag.rel = rel
    if (extra) {
      Object.entries(extra).forEach(([key, value]) => {
        tag.setAttribute(key, value)
      })
    }
    document.head.appendChild(tag)
  }
  
  tag.href = href
}

/**
 * Helper to set JSON-LD script
 */
function setJsonLd(id: string, data: object) {
  let script = document.getElementById(id) as HTMLScriptElement
  
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  
  script.textContent = JSON.stringify(data)
}

/**
 * Remove all hreflang tags (to re-add fresh ones)
 */
function clearHreflangTags() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove())
}

/**
 * Main SEO Hook
 */
export function useSEO(options: UseSEOOptions) {
  const {
    title,
    description,
    keywords = [],
    language,
    path,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    noindex = false,
    breadcrumbs,
    faq,
    article,
  } = options

  useEffect(() => {
    // Title
    document.title = title

    // Basic meta tags
    setMetaTag('description', description)
    if (keywords.length > 0) {
      setMetaTag('keywords', keywords.join(', '))
    }

    // Robots
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow')
    } else {
      setMetaTag('robots', 'index, follow')
    }

    // Language
    document.documentElement.lang = language === 'pt' ? 'pt-BR' : language

    // Canonical
    const canonicalUrl = getCanonicalUrl(path)
    setLinkTag('canonical', canonicalUrl)

    // Open Graph
    setMetaTag('og:title', title, true)
    setMetaTag('og:description', description, true)
    setMetaTag('og:image', ogImage, true)
    setMetaTag('og:url', canonicalUrl, true)
    setMetaTag('og:type', ogType, true)
    setMetaTag('og:site_name', 'PinDay', true)
    setMetaTag('og:locale', LANGUAGE_LOCALES[language], true)

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', title)
    setMetaTag('twitter:description', description)
    setMetaTag('twitter:image', ogImage)

    // Article specific (for blog posts)
    if (article) {
      if (article.publishedTime) {
        setMetaTag('article:published_time', article.publishedTime, true)
      }
      if (article.modifiedTime) {
        setMetaTag('article:modified_time', article.modifiedTime, true)
      }
      if (article.author) {
        setMetaTag('article:author', article.author, true)
      }
    }

    // Hreflang tags
    clearHreflangTags()
    // Extract the path without locale prefix (e.g., /en/solutions/photographers → /solutions/photographers)
    const pathWithoutLocale = path.replace(/^\/(en|pt|es)/, '')
    
    // Generate hreflang for each language
    const supportedLanguages: SupportedLanguage[] = ['en', 'pt', 'es']
    supportedLanguages.forEach(lang => {
      const hreflangValue = lang === 'pt' ? 'pt-BR' : lang === 'en' ? 'en' : 'es'
      const hrefValue = `${SITE_URL}/${lang}${pathWithoutLocale}`
      setLinkTag('alternate', hrefValue, { hreflang: hreflangValue })
    })
    
    // Add x-default (English)
    setLinkTag('alternate', `${SITE_URL}/en${pathWithoutLocale}`, { hreflang: 'x-default' })

    // Schema.org - Organization (always present)
    setJsonLd('schema-org-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      ...SCHEMA_ORG_CONFIG.organization,
    })

    // Schema.org - Software Application
    setJsonLd('schema-org-software', {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      ...SCHEMA_ORG_CONFIG.softwareApplication,
    })

    // Schema.org - Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd('schema-org-breadcrumbs', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      })
    }

    // Schema.org - FAQ
    if (faq && faq.length > 0) {
      setJsonLd('schema-org-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      })
    }

    // Cleanup on unmount
    return () => {
      // Keep basic SEO tags but remove page-specific ones
      document.getElementById('schema-org-breadcrumbs')?.remove()
      document.getElementById('schema-org-faq')?.remove()
    }
  }, [title, description, keywords, language, path, ogImage, ogType, noindex, breadcrumbs, faq, article])
}

export default useSEO
